import { storeToRefs } from "pinia"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import type {
  IOutgoingInvoice,
  IOutgoingInvoiceReadiness,
  IOutgoingInvoiceRequest
} from "@/interfaces/api/outgoing-invoice.interface"
import { OutgoingInvoice } from "@/models/outgoing-invoice.model"
import { OutgoingInvoiceRepository } from "@/repositories/outgoing-invoice-repository"
import { useOutgoingInvoiceStore } from "@/store/outgoing-invoices.store"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import { OutgoingInvoiceStatuses } from "@/enums/models/outgoing-invoice-statuses"
import type { OutgoingInvoiceVatRates } from "@/enums/models/outgoing-invoice-vat-rates"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

/**
 * Invoices are never created or edited through the generic CRUD: the server makes them (Factureren, Crediteren)
 * and moves their state. This controller keeps the store in sync with the socket, like the creation jobs, and
 * adds the results of our own requests, since our own socket messages are ignored.
 */
export class OutgoingInvoiceController extends GenericModelController<
  OutgoingInvoice,
  IOutgoingInvoice,
  OutgoingInvoiceRepository
> {
  constructor() {
    const outgoingInvoiceStore = useOutgoingInvoiceStore()
    const { outgoingInvoices } = storeToRefs(outgoingInvoiceStore)

    super(OutgoingInvoice, { collection: outgoingInvoices }, new OutgoingInvoiceRepository())

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      OutgoingInvoice,
      outgoingInvoices
    )
  }

  /** The invoices still being generated or mailed, plus the failed ones: the "Verzenden" panel. */
  public async retrieveUnfinishedInvoices(): Promise<OutgoingInvoice[]> {
    const query = new ODataQueryBuilder()
      .in("status", [
        OutgoingInvoiceStatuses.PENDING,
        OutgoingInvoiceStatuses.GENERATING,
        OutgoingInvoiceStatuses.SENDING,
        OutgoingInvoiceStatuses.FAILED
      ])
      .orderBy("created_at", "asc")
    return await this.getList({ query })
  }

  public async readiness(approvalIds: string[]): Promise<IOutgoingInvoiceReadiness[]> {
    return await this.apiFunctions.getRepository().readiness(approvalIds)
  }

  public async previewHtml(
    approvalId: string,
    vatRate?: OutgoingInvoiceVatRates | null
  ): Promise<string> {
    return await this.apiFunctions.getRepository().previewHtml(approvalId, vatRate)
  }

  public async html(invoice: OutgoingInvoice): Promise<string> {
    return await this.apiFunctions.getRepository().html(invoice.id!)
  }

  public async invoiceApprovals(
    rows: IOutgoingInvoiceRequest[],
    requestConfig?: IRepositoryRequestConfig
  ): Promise<OutgoingInvoice[]> {
    const invoices = await this.apiFunctions.getRepository().bulkCreateInvoices(rows, requestConfig)
    return (await this.storeFunctions.createModel(invoices)) as OutgoingInvoice[]
  }

  public async credit(
    invoice: OutgoingInvoice,
    sendEmail: boolean,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<OutgoingInvoice> {
    const credit = await this.apiFunctions
      .getRepository()
      .credit(invoice.id!, sendEmail, requestConfig)
    // The credited invoice itself did not change, but its is_credited flag did; the server broadcasts the
    // approval, so only the new credit needs adding here.
    return await this.storeFunctions.createModel(credit)
  }

  public async retry(invoice: OutgoingInvoice): Promise<OutgoingInvoice> {
    const retried = await this.apiFunctions.getRepository().retry(invoice.id!)
    return this.storeFunctions.updateModel(retried)
  }
}
